import { Filters, Loader } from 'components';
import content from 'content.json';
import React, { useEffect, useRef, useState } from 'react';
import { Badge, Col, Container, Image, Row } from 'react-bootstrap';
import InfiniteScroll from 'react-infinite-scroll-component';
import { Link, useParams } from 'react-router-dom';
import { Formatter } from 'services';
import styled from 'styled-components';
import { Collection, CollectionItem } from 'types/collection';
import {
    BoardsQueryParameters,
    FilterOptionsParameters,
    FilterParams,
    FilterStruct
} from 'types/styling';

import { Empty } from '../components';
import { TRouteParams } from '../types';

const { styling } = content;

const SubTabsNav = styled.div`
    display: flex;
    align-items: center;

    a {
        color: #000000;
        margin-right: 23px;
        font-size: 14px;

        &.current,
        &:hover {
            font-weight: bold;
            color: #000000;
        }
    }
`;

interface IBoards {
    loading: boolean;
    loadingFilters: () => void | undefined;
    boards: Collection[];
    clearBoards: () => void;
    loadBoards: (input: BoardsQueryParameters) => void;
    loadBoardsFilters: (fields: Record<string, string>) => void;
    filters: FilterStruct[];
    updateFilters: (filter: FilterParams, isSelected: boolean, callback: () => void) => void;
    selectedFilters: Record<string, FilterOptionsParameters[]>;
    addToCanvas: (item: CollectionItem) => void;
}

export const Boards: React.FC<IBoards> = ({
    loading,
    loadingFilters,
    boards = [],
    clearBoards,
    loadBoards,
    loadBoardsFilters,
    filters,
    selectedFilters,
    updateFilters,
    addToCanvas
}) => {
    const { subTab = 'look' } = useParams<TRouteParams>();
    const [jump, setJump] = useState(false);
    const resultsRef = useRef<HTMLDivElement>(null);
    const [gridItems, setGridItems] = useState<Collection[]>([]);

    useEffect(() => {
        loadBoardsFilters({});
        clearBoards();
    }, []);

    useEffect(() => {
        fetchData({
            type: [{ key: subTab, text: subTab }]
        });
    }, [subTab]);

    useEffect(() => {
        if (!loading && boards) setGridItems(boards);
    }, [boards]);

    const fetchData = (filters?: Record<string, FilterOptionsParameters[]>) => {
        const activeFilters = filters ? filters : selectedFilters;
        const params = Object.keys(activeFilters).reduce(
            (ac, a) =>
                activeFilters[a].length
                    ? {
                          ...ac,
                          [a]: activeFilters[a]
                              .map((selection: FilterOptionsParameters) => selection.key)
                              .join(',')
                      }
                    : ac,
            {}
        );
        loadBoards({
            ...params,
            from: gridItems.length > 0 ? gridItems.length : 1,
            type: subTab
        });
    };

    const onFilterChange = (filter: FilterParams) => {
        const isSelected =
            !!selectedFilters[filter.key] &&
            selectedFilters[filter.key].findIndex((item: FilterParams | FilterOptionsParameters) =>
                !Array.isArray(filter.value) ? item.key === filter.value.key : false
            ) > -1;
        updateFilters(filter, isSelected, fetchData);
    };

    return (
        <Container className="styling-room-boards" fluid>
            <Row>
                <Col className="filters-column">
                    {loadingFilters == undefined && <Loader />}
                    <p className="total">
                        {styling.boards.replace('%count%', Formatter.number(gridItems.length))}
                    </p>
                    <Filters
                        className={
                            selectedFilters.category && !selectedFilters.category.length
                                ? 'categories-all'
                                : ''
                        }
                        filters={filters}
                        selected={selectedFilters}
                        setFilter={onFilterChange}
                        showSelection={true}
                    />
                </Col>

                <div id="results" className="results col" ref={resultsRef}>
                    {loading && gridItems && gridItems.length < 1 && <Loader />}
                    <div className="results-bar">
                        <SubTabsNav>
                            {styling.subTabs.boards.map(({ value, text }) => (
                                <Link
                                    key={value}
                                    className={value === subTab ? 'current' : ''}
                                    to={`${value}`}>
                                    {text}
                                </Link>
                            ))}
                        </SubTabsNav>

                        <div className="filter-badges">
                            {Object.keys(selectedFilters).map((key) =>
                                selectedFilters[key].map((filter) => (
                                    <Badge variant="light" key={filter.key}>
                                        <div
                                            className="close"
                                            onClick={() => onFilterChange({ key, value: filter })}
                                        />
                                        {filter.text}
                                    </Badge>
                                ))
                            )}
                        </div>
                    </div>
                    {gridItems && gridItems.length ? (
                        <InfiniteScroll
                            dataLength={gridItems ? gridItems.length : 0}
                            onScroll={() =>
                                setJump(
                                    resultsRef.current ? resultsRef.current.scrollTop > 0 : false
                                )
                            }
                            next={fetchData}
                            hasMore={true}
                            style={{ overflow: 'hidden' }}
                            loader={undefined}>
                            {gridItems.map((board, i, array) =>
                                array.length - 1 === i ? (
                                    <>
                                        <div key={board.uuid} className="board">
                                            <Image
                                                src={board.picture}
                                                onClick={() =>
                                                    board.items.forEach((item) => addToCanvas(item))
                                                }
                                            />
                                        </div>
                                        <div
                                            key="loading"
                                            className="board loading"
                                            style={{
                                                visibility:
                                                    loading && gridItems && gridItems.length > 0
                                                        ? 'visible'
                                                        : 'hidden'
                                            }}>
                                            <Image src={styling.assets.loading_logo} />
                                        </div>
                                    </>
                                ) : (
                                    <div key={board.uuid} className="board">
                                        <Image
                                            src={board.picture}
                                            onClick={() =>
                                                board.items.forEach((item) => addToCanvas(item))
                                            }
                                        />
                                    </div>
                                )
                            )}
                        </InfiniteScroll>
                    ) : (
                        <Empty tab={subTab} />
                    )}
                </div>
            </Row>
            <div
                className={`top-btn ${jump ? 'show' : 'hide'}`}
                onClick={() => (resultsRef.current ? resultsRef.current.scrollTo(0, 0) : null)}>
                {styling.top}
            </div>
        </Container>
    );
};
